import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import {
  Sliders,
  CheckCircle2,
  ArrowRight,
  Package,
  Layers,
  Sparkles,
  Info,
  Scale,
  Ruler,
  Droplet,
  Flame,
  Palette,
  Leaf,
  Sparkle,
} from "lucide-react";

const Specifications = () => {
  const { t, lPath, isRTL } = useLanguage();

  const specCategories = [
    {
      id: "micron",
      name: "Micron (Fibre Diameter)",
      icon: <Sliders className="w-5 h-5 text-wool-bronze" />,
      range: "30+ Micron & 32–34+ Micron Focus",
      importance: "Critical for carpet yarn resilience, bulk, and pile recovery.",
      details:
        "Fibre fineness measured in microns. Carpet yarn manufacturing traditionally relies on robust 30+ and 32–34+ micron wools for superior crimp and durability.",
    },
    {
      id: "staple_length",
      name: "Staple Length",
      icon: <Ruler className="w-5 h-5 text-wool-bronze" />,
      range: "50mm – 100mm+ Variations",
      importance: "Determines carding efficiency, spinning tensile strength, and minimal yarn wastage.",
      details:
        "Consistent staple length ensures that yarn can be spun on woolen or semi-worsted spinning systems with balanced tensile properties.",
    },
    {
      id: "yield",
      name: "Clean Yield %",
      icon: <Scale className="w-5 h-5 text-wool-bronze" />,
      range: "Optimized Clean Wool Content",
      importance: "Directly impacts processing costs, scouring efficiency, and net usable fibre weight.",
      details:
        "Accurate yield assessment through commercial test reports ensures predictable production economics for spinning mills.",
    },
    {
      id: "strength",
      name: "Fibre Tensile Strength & Crimp",
      icon: <Flame className="w-5 h-5 text-wool-bronze" />,
      range: "High Resilience & Elasticity",
      importance: "Withstands high-speed carding, twisting, and heavy carpet foot-traffic.",
      details:
        "Strong natural crimp gives carpet yarns exceptional spring-back, wear resistance, and luxurious hand-feel.",
    },
    {
      id: "colour",
      name: "Colour & Whiteness",
      icon: <Palette className="w-5 h-5 text-wool-bronze" />,
      range: "Natural White, Cream, and Tint Grades",
      importance: "Essential for exact shade matching, dyeing brilliance, or natural un-dyed carpets.",
      details:
        "We grade lots carefully to ensure colour uniformity across large commercial batches.",
    },
    {
      id: "vm",
      name: "Vegetable Matter (VM)",
      icon: <Leaf className="w-5 h-5 text-wool-bronze" />,
      range: "Controlled Low VM Content",
      importance: "Reduces carding breakages and ensures a smooth, clean surface on finished yarns.",
      details:
        "Assessment of burrs, seeds, and grass matter to match the mill's opening, scouring, and carbonizing capabilities.",
    },
    {
      id: "cleanliness",
      name: "Cleanliness & Condition",
      icon: <Droplet className="w-5 h-5 text-wool-bronze" />,
      range: "Dust & Impurity Controlled",
      importance: "Ensures optimal post-scouring clean yields without heavy residue buildup.",
      details:
        "Evaluation of sand, dust, and natural grease content for predictable industrial processing.",
    },
    {
      id: "condition",
      name: "Supply Condition",
      icon: <Package className="w-5 h-5 text-wool-bronze" />,
      range: "Greasy Bales / Scoured Lots",
      importance: "Flexibility according to your mill's internal scouring infrastructure.",
      details:
        "Available as raw unwashed greasy wool in original compressed bales, or pre-scoured and dried commercial lots.",
    },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Wool Specifications",
        "item": "https://skwoolens.trivanahospitality.com/en/wool-specifications"
      }
    ]
  };

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="Carpet Wool Specifications (30+ & 32-34+ Micron) | S.K. Woollens"
        description="Technical raw wool parameters for spinning mills: 30+ & 32-34+ micron fibre diameter, 50-100mm staple length, clean yield testing, VM limits & greasy/scoured lots."
        keywords="30 micron wool, 32 micron wool, 34 micron wool, carpet wool specifications, wool staple length, wool clean yield testing, raw wool parameters Bikaner"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/wool-specifications"
        schema={[breadcrumbSchema]}
      />
      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/20 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="max-w-3xl space-y-4">
            <div className="wool-badge">
              <Sliders className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Technical Wool Matrix</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
              {t("specs.title")}
            </h1>
            <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
              {t("specs.subtitle")}
            </p>

            <div className="p-4 bg-wool-sand/60 border-s-4 border-wool-bronze rounded-e-xl text-xs sm:text-sm font-semibold text-wool-charcoal leading-relaxed">
              {t("specs.carpetHighlight")}
            </div>
          </div>
        </div>
      </section>

      {/* 2. Target Market Focus: 30+ & 32-34+ Micron Carpet Wool */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-white border border-wool-border rounded-3xl p-6 sm:p-10 shadow-subtle">
          <div className="lg:col-span-7 space-y-4">
            <div className="wool-badge">
              <Sparkles className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Carpet Yarn Focus</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
              30+ Micron & 32–34+ Micron Specialization
            </h2>
            <p className="text-sm text-wool-slate leading-relaxed">
              Carpet yarn spinners require specific wool characteristics that deliver high bulk, exceptional resilience, and longevity under heavy wear.
            </p>
            <p className="text-xs sm:text-sm text-wool-muted leading-relaxed">
              We source raw wool tailored to individual spinning mill requirements, focusing on:
            </p>

            <ul className="space-y-2 text-xs sm:text-sm text-wool-charcoal">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-wool-bronze" />
                <span><strong>Coarse & Medium Wool Grades:</strong> 30+ micron and 32–34+ micron resilience.</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-wool-bronze" />
                <span><strong>Optimal Staple Lengths:</strong> Balanced for woolen and semi-worsted spinning.</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-wool-bronze" />
                <span><strong>Dependable Batch Uniformity:</strong> Consistent lot-to-lot procurement.</span>
              </li>
            </ul>

            <div className="pt-2">
              <a
                href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20connect%20with%20you%20regarding%20wool%20sourcing%20and%20supply"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-xs inline-flex items-center gap-2"
              >
                <span>Connect on WhatsApp</span>
                <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} />
              </a>
            </div>
          </div>

          {/* Interactive Micron Visualizer */}
          <div className="lg:col-span-5 bg-gradient-to-br from-wool-sand/20 to-wool-bg border border-wool-border rounded-2xl p-6 shadow-md flex flex-col justify-between h-[360px] relative select-none">
            <div className="text-center pb-2 border-b border-wool-border/60">
              <span className="text-xs font-bold text-wool-bronze uppercase tracking-wider">Fibre Diameter Zoom View</span>
            </div>
            
            <div className="flex justify-around items-end flex-1 py-8 relative">
              {/* 30+ Micron Strand */}
              <div className="flex flex-col items-center group cursor-pointer">
                {/* Cylinder */}
                <div className="w-6 bg-gradient-to-r from-wool-border via-wool-sand to-wool-border border border-wool-border/80 rounded-full h-44 shadow-sm group-hover:scale-105 group-hover:border-wool-bronze/50 transition-all duration-300 relative flex items-center justify-center">
                  {/* Subtle internal fiber crimp line */}
                  <div className="absolute inset-y-0 w-px border-l border-dashed border-wool-bronze/20"></div>
                </div>
                <span className="text-xs font-bold text-wool-charcoal mt-3">30+ Micron</span>
                <span className="text-[10px] text-wool-muted">Medium Carpet Grade</span>
              </div>

              {/* Zoom Center Highlight Indicator */}
              <div className="absolute inset-y-0 w-px bg-dashed border-l border-wool-border/60 left-1/2 hidden md:block"></div>

              {/* 32-34+ Micron Strand */}
              <div className="flex flex-col items-center group cursor-pointer">
                {/* Cylinder (Thicker) */}
                <div className="w-9 bg-gradient-to-r from-wool-border via-wool-sand to-wool-border border border-wool-border/80 rounded-full h-44 shadow-sm group-hover:scale-105 group-hover:border-wool-bronze/50 transition-all duration-300 relative flex items-center justify-center">
                  {/* Subtle internal fiber crimp line */}
                  <div className="absolute inset-y-0 w-px border-l border-dashed border-wool-bronze/20"></div>
                </div>
                <span className="text-xs font-bold text-wool-charcoal mt-3">32–34+ Micron</span>
                <span className="text-[10px] text-wool-muted">Coarse Carpet Grade</span>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-xl border border-wool-border/60 text-center text-[10px] text-wool-slate">
              <span className="font-semibold text-wool-bronze">Visual comparison only: </span> 
              32–34+ micron fibers yield higher pile bulk, while 30+ micron fibers provide soft surface touch.
            </div>
          </div>
        </div>
      </section>

      {/* Microscopic Zoom Visual Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-wool-charcoal text-white rounded-3xl p-8 sm:p-14 shadow-premium relative overflow-hidden">
          {/* Subtle background scale pattern */}
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <pattern id="scales" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M0,20 C10,10 30,10 40,20 C30,30 10,30 0,20 Z" fill="none" stroke="#FFFFFF" strokeWidth="1" />
              </pattern>
              <rect width="100%" height="100%" fill="url(#scales)" />
            </svg>
          </div>

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4 text-start">
              <div className="wool-badge bg-white/10 text-wool-sand border-white/20">
                <Sparkle className="w-3.5 h-3.5 text-wool-sand" />
                <span>Microscopic View</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-display text-white">
                Zoom Into the Wool Fibre Cuticle Structure
              </h2>
              <p className="text-xs sm:text-sm text-wool-sand/80 leading-relaxed">
                Under magnification, a wool fibre is not smooth. It is covered in overlapping protein scales (the cuticle cell layers) pointing towards the tip.
              </p>
              <p className="text-xs sm:text-sm text-wool-sand/70 leading-relaxed">
                These scales slide against each other, creating a mechanical interlock that holds carded fibers together. This natural scale architecture is what enables wool to be spun into strong carpet yarns and resist felting during wear.
              </p>
            </div>

            <div className="lg:col-span-5 flex items-center justify-center p-4">
              <div className="relative w-full max-w-sm aspect-video bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col justify-center items-center shadow-inner group hover:border-wool-sand/40 transition">
                {/* Stylized microscopic strand using CSS overlapping shapes */}
                <div className="flex gap-1.5 justify-center items-center h-12 w-full max-w-xs relative bg-white/10 rounded-full border border-white/15 px-4 overflow-hidden">
                  <div className="w-6 h-6 rounded-full bg-wool-sand/40 border border-white/25 -mr-2 shadow-sm animate-pulse"></div>
                  <div className="w-6 h-6 rounded-full bg-wool-sand/55 border border-white/25 -mr-2 shadow-sm animate-pulse delay-75"></div>
                  <div className="w-6 h-6 rounded-full bg-wool-sand/70 border border-white/25 -mr-2 shadow-sm animate-pulse delay-150"></div>
                  <div className="w-6 h-6 rounded-full bg-wool-sand/85 border border-white/25 -mr-2 shadow-sm animate-pulse delay-300"></div>
                  <div className="w-6 h-6 rounded-full bg-white border border-white/25 shadow-sm animate-pulse delay-500"></div>
                </div>
                <div className="text-[11px] text-wool-sand/85 font-medium tracking-wide uppercase mt-4 text-center">
                  Tactile Overlapping Scales (Cuticles)
                </div>
                <div className="text-[10px] text-wool-sand/50 mt-1">
                  Enables friction-spin cohesion in carding mills.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Comprehensive 8-Parameter Specification Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto space-y-2 mb-10">
          <div className="wool-badge">
            <Layers className="w-3.5 h-3.5 text-wool-bronze" />
            <span>Technical Parameters</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
            How We Evaluate Raw Wool Lots
          </h2>
          <p className="text-xs sm:text-sm text-wool-muted">
            Every bale and consignment is appraised against key commercial and physical attributes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {specCategories.map((spec) => (
            <div
              key={spec.id}
              className="wool-card p-6 space-y-3 bg-white flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
                  {spec.icon}
                </div>
                <h3 className="text-base font-bold text-wool-charcoal">
                  {spec.name}
                </h3>
                <div className="inline-block px-2 py-0.5 bg-wool-bg text-wool-bronze font-semibold text-[11px] rounded border border-wool-border/80">
                  {spec.range}
                </div>
                <p className="text-xs text-wool-slate leading-relaxed">
                  {spec.details}
                </p>
              </div>

              <div className="pt-3 border-t border-wool-border/60 text-[11px] text-wool-muted italic">
                {spec.importance}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. Technical Disclaimer & Next Steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-wool-charcoal text-white rounded-3xl p-8 sm:p-12 shadow-xl space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <Info className="w-5 h-5 text-wool-sand" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-bold font-display">
                Specification-Based Procurement Policy
              </h3>
              <p className="text-xs sm:text-sm text-wool-sand/80 leading-relaxed max-w-3xl">
                {t("specs.disclaimer")} We do not apply a generic standard to all buyers; instead, our procurement desk works with your exact specification sheet (micron, staple length, yield, and VM tolerances) to deliver consistent, spinning-ready consignments.
              </p>
            </div>
          </div>

          <div className="pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
            <span className="text-xs text-wool-sand/70">
              Have specific test parameters or a target blend?
            </span>
            <Link
              to={lPath("/for-yarn-manufacturers")}
              className="btn-primary bg-wool-sand text-wool-charcoal hover:bg-white text-xs"
            >
              <span>Submit Yarn Mill Specifications</span>
              <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Specifications;
